import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Chip, Divider, IconButton, Tooltip,
  Modal, Button, Fade, Backdrop, Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import formatThaiDate from '../../utils/functions/formatThaiDate';
import { ACTIVE_STATUSES, statusChip } from '../../utils/constants/financeStatus.constant';
import ImageUpload from '../ImageUpload/ImageUpload';
import * as actions from '../../redux/actions';
import { DevWinBadge } from '../DevPageBadge';

export default function CompletePaymentModal({ open, onClose, payment }) {
  const dispatch = useDispatch();
  const expenseRows = useSelector((state) => state.expenses?.rows || []);

  const [slipImages, setSlipImages] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [note, setNote] = useState('');
  const [copySnack, setCopySnack] = useState({ open: false, text: '' });
  const me = useSelector((state) => state.me);

  const handleCopy = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopySnack({ open: true, text: `คัดลอก ${label}: ${value}` });
    });
  };

  const totalAmount = payment?.totalAmount || 0;
  const alreadyPaid = payment?.paid_amount || 0;
  const remaining = Math.max(0, totalAmount - alreadyPaid);
  const payLogs = payment?.payment_logs || [];

  // sync payAmount กับ remaining ทุกครั้งที่ payment เปลี่ยน
  React.useEffect(() => {
    if (open) {
      setPayAmount(remaining > 0 ? remaining.toFixed(2) : '0');
      setSlipImages([]);
      setNote('');
    }
  }, [open, payment?._id]);

  // ดึง expense rows ที่อยู่ใน payment นี้
  const expenseIds = React.useMemo(
    () => new Set((payment?.expenses_list || []).map((e) => String(e.expenses_id))),
    [payment],
  );
  const expensesInPayment = React.useMemo(
    () => expenseRows.filter((e) => expenseIds.has(String(e._id))),
    [expenseRows, expenseIds],
  );

  const payNow = parseFloat(payAmount) || 0;
  const willBeFullyPaid = payNow >= remaining - 0.001 && remaining > 0;

  const fmt = (n) => (n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

  const handleConfirm = async () => {
    if (!payment?._id) return;
    if (payNow <= 0) { alert('กรุณาระบุจำนวนเงินที่ต้องการจ่าย'); return; }
    if (payNow > remaining + 0.001) { alert(`จำนวนเงินเกินยอดคงค้าง (${fmt(remaining)} ฿)`); return; }
    const confirmMsg = willBeFullyPaid
      ? 'ยืนยันจ่ายเงินครบ? (status จะเปลี่ยนเป็น SUCCESS)'
      : `ยืนยันจ่ายบางส่วน ฿${fmt(payNow)}? (ยังคงค้าง ฿${fmt(remaining - payNow)})`;
    if (!window.confirm(confirmMsg)) return;
    setCompleting(true);
    try {
      const paidByName = `${me?.userData?.firstname || ''} ${me?.userData?.lastname || ''}`.trim();
      await dispatch(actions.paymentComplete(payment._id, {
        amount: payNow,
        slipImage: slipImages,
        note,
        paid_by_name: paidByName,
      }));
      // fetch ALL payments (ไม่กรอง notStatus) เพื่อให้ FinanceSummary ยังเห็น SUCCESS payments ของวันนี้
      dispatch(actions.paymentAll({ size: 9999, page: 1 }));
      dispatch(actions.expensesAll({ statuses: ACTIVE_STATUSES, size: 9999, page: 1 }));
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
    <DevWinBadge num={140} name="CompletePaymentModal" open={open} />
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 300 } }}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute',
top: '50%',
left: '50%',
transform: 'translate(-50%, -50%)',
          width: '90vw',
maxWidth: 760,
maxHeight: '92vh',
bgcolor: 'background.paper',
          borderRadius: 2,
boxShadow: 24,
display: 'flex',
flexDirection: 'column',
overflow: 'hidden',
        }}>
          {/* Header */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight={700} color="#15803d">จ่ายเงิน</Typography>
              {payment && (
                <Typography variant="body2" color="text.secondary">
                  {payment.payment_number} · ผู้รับ: {payment.payee?.name}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Box>

          {/* ข้อมูลผู้รับเงิน */}
          <Box sx={{ px: 3, py: 1.5, backgroundColor: '#faf5ff', borderBottom: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* ผู้รับเงิน */}
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>ผู้รับเงิน</Typography>
                <Tooltip title={payment?.payee?.name || '-'} arrow placement="top">
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {payment?.payee?.name || '-'}
                  </Typography>
                </Tooltip>
              </Box>
              <Divider orientation="vertical" flexItem />
              {/* เลขบัญชี (copy) */}
              <Tooltip title="ดับเบิลคลิกเพื่อคัดลอก" arrow>
                <Box
                  onDoubleClick={() => handleCopy(payment?.payee?.account_number, 'เลขบัญชี')}
                  sx={{ cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { backgroundColor: '#ede9fe' }, transition: 'background-color 0.15s' }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>เลขบัญชี</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#4338ca">{payment?.payee?.account_number || '-'}</Typography>
                    <ContentCopyIcon sx={{ fontSize: 12, color: '#a5b4fc' }} />
                  </Box>
                </Box>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              {/* ธนาคาร */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>ธนาคาร</Typography>
                <Typography variant="body2" fontWeight={500} color="text.secondary">{payment?.payee?.bank || '-'}</Typography>
              </Box>
            </Box>
          </Box>

          {/* ยอดสรุป */}
          <Box sx={{ px: 3, py: 1.5, backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">ยอดรวมทั้งหมด</Typography>
              <Typography variant="body1" fontWeight={700}>฿{fmt(totalAmount)}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">จ่ายแล้ว</Typography>
              <Typography variant="body1" fontWeight={700} color="#15803d">฿{fmt(alreadyPaid)}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            {/* ยอดคงค้าง (copy) */}
            <Tooltip title="ดับเบิลคลิกเพื่อคัดลอกยอดคงค้าง" arrow>
              <Box
                onDoubleClick={() => handleCopy(remaining.toFixed(2), 'ยอดคงค้าง')}
                sx={{ textAlign: 'center', cursor: 'pointer', borderRadius: 1, px: 1, '&:hover': { backgroundColor: '#fef2f2' }, transition: 'background-color 0.15s' }}
              >
                <Typography variant="caption" color="text.secondary" display="block">ยอดคงค้าง</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                  <Typography variant="body1" fontWeight={700} color={remaining > 0 ? '#dc2626' : '#15803d'}>฿{fmt(remaining)}</Typography>
                  {remaining > 0 && <ContentCopyIcon sx={{ fontSize: 12, color: '#fca5a5' }} />}
                </Box>
              </Box>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            {statusChip(payment?.status)}
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
            {/* ประวัติการจ่าย */}
            {payLogs.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>ประวัติการจ่าย ({payLogs.length} ครั้ง)</Typography>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                  {payLogs.map((log, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: i < payLogs.length - 1 ? '1px solid #f1f5f9' : 'none', backgroundColor: '#f9fafb' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="#15803d">฿{fmt(log.amount)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.paid_by_name || '-'} · {log.paid_at ? formatThaiDate(log.paid_at) : '-'}
                          {log.note ? ` · ${log.note}` : ''}
                        </Typography>
                      </Box>
                      <Chip label={`ครั้งที่ ${i + 1}`} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: '#dbeafe', color: '#1d4ed8', '& .MuiChip-label': { px: 0.6 } }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* รายการ expenses */}
            <Typography variant="subtitle2" fontWeight={700} mb={1}>รายการเบิกใน Payment ({expensesInPayment.length} รายการ)</Typography>
            <Box sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
              {expensesInPayment.length === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}><Typography variant="body2" color="text.secondary">ไม่มีรายการ</Typography></Box>
              ) : expensesInPayment.map((e, i) => {
                const expStatusColor = {
                  SUCCESS: { bg: '#dcfce7', color: '#15803d' },
                  PREPARE: { bg: '#dbeafe', color: '#1d4ed8' },
                  APPROVE: { bg: '#d1fae5', color: '#065f46' },
                  PENDING: { bg: '#fef9c3', color: '#92400e' },
                }[e.status] || { bg: '#f3f4f6', color: '#374151' };
                return (
                  <Box key={e._id} sx={{ px: 2, py: 1.5, borderBottom: i < expensesInPayment.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { backgroundColor: '#f9fafb' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} color="#1d4ed8" sx={{ flexShrink: 0 }}>{e.code || '-'}</Typography>
                      <Chip label={e.status || '-'} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: expStatusColor.bg, color: expStatusColor.color, borderRadius: 0.5, '& .MuiChip-label': { px: 0.6 } }} />
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="body2" fontWeight={700} color="#1d4ed8">฿{fmt(e.net_price ?? e.price)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                      <Typography variant="body2" color="text.primary" sx={{ flexGrow: 1 }}>{e.name || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{formatThaiDate(e.dateRequest || e.date)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {e.project?.name && <Typography variant="caption" color="text.secondary">📁 {e.project.name}</Typography>}
                      {(e.budget?.budget_number || e.budget?.prefix) && <Typography variant="caption" color="text.secondary">· งบ: {e.budget?.prefix || ''}{e.budget?.budget_number || ''}</Typography>}
                      {e.invoice_number && <Typography variant="caption" color="text.secondary">· Invoice: {e.invoice_number}</Typography>}
                      {e.vat_amount > 0 && <Typography variant="caption" color="text.secondary">· VAT: ฿{fmt(e.vat_amount)}</Typography>}
                      {e.withholding_tax > 0 && <Typography variant="caption" color="#b45309">· หัก ณ ที่จ่าย: ฿{fmt(e.withholding_tax)}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* กรอกจำนวนที่จ่ายครั้งนี้ */}
            {remaining > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>จำนวนเงินที่จ่ายครั้งนี้</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Box sx={{ flexGrow: 1, minWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>จำนวนเงิน (฿) <span style={{ color: '#dc2626' }}>*</span></Typography>
                    <Box
                      component="input"
                      type="number"
                      value={payAmount}
                      onChange={(ev) => setPayAmount(ev.target.value)}
                      min={0}
                      max={remaining}
                      step="0.01"
                      style={{
                        width: '100%',
padding: '8px 12px',
fontSize: '1rem',
fontWeight: 700,
                        border: '1.5px solid #d1d5db',
borderRadius: 8,
outline: 'none',
                        color: willBeFullyPaid ? '#15803d' : '#1d4ed8',
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.8 }}>
                      <Button size="small" variant="outlined" onClick={() => setPayAmount(remaining.toFixed(2))}
                        sx={{ fontSize: '0.68rem', py: 0.3, px: 1 }}>จ่ายครบ</Button>
                      {remaining > 0 && (
                        <Button size="small" variant="outlined" onClick={() => setPayAmount((remaining / 2).toFixed(2))}
                          sx={{ fontSize: '0.68rem', py: 0.3, px: 1 }}>ครึ่งหนึ่ง</Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flexGrow: 2, minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>หมายเหตุ (ถ้ามี)</Typography>
                    <Box
                      component="input"
                      type="text"
                      value={note}
                      onChange={(ev) => setNote(ev.target.value)}
                      placeholder="เช่น โอนผ่าน KTB..."
                      style={{
                        width: '100%',
padding: '8px 12px',
fontSize: '0.9rem',
                        border: '1.5px solid #d1d5db',
borderRadius: 8,
outline: 'none',
                      }}
                    />
                  </Box>
                </Box>
                {payNow > 0 && (
                  <Box sx={{ mt: 1.5, px: 2, py: 1, borderRadius: 1, backgroundColor: willBeFullyPaid ? '#f0fdf4' : '#eff6ff', border: `1px solid ${willBeFullyPaid ? '#86efac' : '#bfdbfe'}` }}>
                    <Typography variant="body2" fontWeight={600} color={willBeFullyPaid ? '#15803d' : '#1d4ed8'}>
                      {willBeFullyPaid
                        ? `จ่ายครบ ฿${fmt(payNow)} → status จะเปลี่ยนเป็น SUCCESS`
                        : `จ่ายบางส่วน ฿${fmt(payNow)} → คงค้างอีก ฿${fmt(remaining - payNow)}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Upload slip */}
            <Typography variant="subtitle2" fontWeight={700} mb={1}>อัปโหลดสลิปการโอน (ถ้ามี)</Typography>
            <ImageUpload
              preview_size="260"
              maxNumber={1}
              images={slipImages}
              setImages={setSlipImages}
              title="เลือกสลิปการโอน"
            />
          </Box>

          {/* Footer */}
          <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={onClose} disabled={completing}>ยกเลิก</Button>
            {remaining > 0 ? (
              <Button variant="contained" onClick={handleConfirm} disabled={completing || payNow <= 0}
                sx={{ backgroundColor: willBeFullyPaid ? '#15803d' : '#1d4ed8', '&:hover': { backgroundColor: willBeFullyPaid ? '#166534' : '#1e40af' } }}>
                {completing ? 'กำลังดำเนินการ...' : willBeFullyPaid ? 'ยืนยันจ่ายครบ' : `จ่ายบางส่วน ฿${fmt(payNow)}`}
              </Button>
            ) : (
              <Button variant="contained" disabled sx={{ backgroundColor: '#15803d' }}>จ่ายครบแล้ว</Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>

    <Snackbar
      open={copySnack.open}
      autoHideDuration={1500}
      onClose={() => setCopySnack({ open: false, text: '' })}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="success" variant="filled" sx={{ fontSize: '0.78rem' }}>
        {copySnack.text}
      </Alert>
    </Snackbar>
    </>
  );
}
